import React, {Fragment} from "react";
import PropTypes from "prop-types";
import moment from "moment";
import {ModuloHRAPI} from "../../../../../api/ModuloHR/ModuloHRAPI";
import FuxOffsetPaginator from "../../../../../components/BaseComponents/FuxOffsetPaginator/FuxOffsetPaginator";
import ModalPortalHOC from "../../../../../components/BaseComponents/ModalPortalHOC";
import AppConfig from "../../../../../config/AppConfig";
import {AppIconThumbnail} from "../../../../../components/BaseComponents/AppIconThumbnail";
import ExpensesListItem from "./ExpensesListItem";
import NewExpensePage from "./NewExpensePage";
import ExpenseDetailPage from "./ExpenseDetailPage";
import {formatCurrency} from "../../../../../helpers/CurrencyHelpers";

export default class ExpensesTab extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            newExpenseOpen: false,
            detailExpense: null,
            selectedStaffId: props.staffList[0]?.id_dipendente || null,
            monthlyTotalsByStaff: {},
            monthlyTotalsLoading: false
        };
        this.paginatorRef = React.createRef();
    }

    componentDidMount() {
        this.fetchMonthlyTotals();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.staffList !== this.props.staffList) {
            this.fetchMonthlyTotals();
        }
    }

    getStaffById = staffId => {
        if (!staffId) return null;
        return (this.props.staffList || []).find(s => String(s.id_dipendente) === String(staffId));
    }

    resetPaginator = _ => {
        if (this.paginatorRef.current && this.paginatorRef.current.reset) {
            this.paginatorRef.current.reset();
        }
    }

    handleNewExpenseOpen = _ => this.setState({newExpenseOpen: true});
    handleNewExpenseClose = _ => this.setState({newExpenseOpen: false});
    handleDetailClose = _ => this.setState({detailExpense: null});

    handlePageRequest = page => {
        const staffIds = (this.props.staffList || []).map(d => d.id_dipendente).filter(Boolean);
        if (!staffIds.length) {
            return Promise.resolve({data: [], total: 0, max_items: 1, pages: 1, prev: null, next: null});
        }
        return ModuloHRAPI.Expenses.pagination(staffIds, page);
    }

    handleExpenseCreated = staffId => {
        this.setState({newExpenseOpen: false, selectedStaffId: staffId || this.state.selectedStaffId}, () => {
            this.resetPaginator();
            this.fetchMonthlyTotals();
        });
    }

    handleExpenseUpdated = _ => {
        this.resetPaginator();
        this.fetchMonthlyTotals();
    }

    handleExpenseDeleted = _ => {
        this.resetPaginator();
        this.fetchMonthlyTotals();
    }

    handleExpenseOpen = item => {
        const staff = this.getStaffById(item.id_dipendente);
        this.setState({detailExpense: {expense: item, staff: staff}});
    }

    renderItem = (item, index) => {
        const staff = this.getStaffById(item.id_dipendente);
        return (
            <ExpensesListItem
                key={item.expense_id || index}
                item={item}
                staff={staff}
                onOpen={this.handleExpenseOpen}
            />
        );
    }

    renderEmptyPage = _ => {
        return (
            <div className={"container text-center text-muted py-4"}>
                Non ci sono spese.
            </div>
        );
    }

    fetchMonthlyTotals = _ => {
        const staffList = this.props.staffList || [];
        if (!staffList.length) {
            this.setState({monthlyTotalsByStaff: {}, monthlyTotalsLoading: false});
            return;
        }

        const startDate = moment().startOf("month").format("YYYY-MM-DD");
        const endDate = moment().endOf("month").format("YYYY-MM-DD");
        this.setState({monthlyTotalsLoading: true});

        Promise.all(staffList.map(staff => {
            return ModuloHRAPI.Expenses.approvedTotalDateRange(staff.id_dipendente, startDate, endDate)
                .then(data => ({staffId: staff.id_dipendente, total: data?.approved_total}))
                .catch(_ => ({staffId: staff.id_dipendente, total: 0}));
        }))
            .then(results => {
                const monthlyTotalsByStaff = {};
                results.forEach(result => {
                    monthlyTotalsByStaff[String(result.staffId)] = parseFloat(result.total) || 0;
                });
                this.setState({monthlyTotalsByStaff: monthlyTotalsByStaff, monthlyTotalsLoading: false});
            })
            .catch(_ => {
                this.setState({monthlyTotalsLoading: false});
            });
    }

    renderMonthlyBudgetBlock = staff => {
        const staffId = String(staff.id_dipendente);
        const approvedTotal = this.state.monthlyTotalsByStaff[staffId] || 0;
        const monthlyBudget = parseFloat(staff.monthly_budget) || 0;
        const currentMonthName = moment().format("MMMM");
        const negozioIconUrl = staff.negozio?.id_negozio
            ? `${AppConfig.webServerUrl}/api/negozio/${staff.negozio.id_negozio}/logoIcon/50`
            : null;

        if (!monthlyBudget) return '';

        return (
            <div key={staff.id_dipendente} className={"card mr-2 mb-2"}>
                <div className={"card-body p-2"}>
                    <div className={"d-flex align-items-center mb-1"}>
                        <AppIconThumbnail size={"20px"} src={negozioIconUrl} className={"mr-1"}/>
                        <div className={"small font-weight-bold"}>Budget {currentMonthName}</div>
                    </div>
                    <div className={"small"}>
                        {formatCurrency(approvedTotal,'EUR','it-IT')} / {formatCurrency(monthlyBudget,'EUR','it-IT')}
                    </div>
                </div>
            </div>
        );
    }

    render() {
        return (
            <Fragment>
                <div className={"container py-3 d-flex align-items-center justify-content-between"}>
                    <div className={"d-flex flex-wrap pr-2"}>
                        {
                            this.state.monthlyTotalsLoading ?
                                <div className={"small text-muted"}>Caricamento budget mensili...</div>
                                :
                                (this.props.staffList || []).map(this.renderMonthlyBudgetBlock)
                        }
                    </div>
                    <div className={"pl-2"}>
                        <button
                            type={"button"}
                            className={"btn btn-primary"}
                            onClick={this.handleNewExpenseOpen}
                        >
                            Nuova spesa
                        </button>
                    </div>
                </div>

                <div className={"container pb-4"}>
                    <FuxOffsetPaginator
                        ref={this.paginatorRef}
                        onPageRequest={this.handlePageRequest}
                        onItemRender={this.renderItem}
                        onEmptyPage={this.renderEmptyPage}
                        classItemsContainer={"list-group"}
                        visiblePagesCount={5}
                    />
                </div>

                <ModalPortalHOC>
                    {
                        this.state.newExpenseOpen &&
                        <NewExpensePage
                            staffList={this.props.staffList}
                            selectedStaffId={this.state.selectedStaffId}
                            active={this.state.newExpenseOpen}
                            onDismiss={this.handleNewExpenseClose}
                            onExpenseCreated={this.handleExpenseCreated}
                        />
                    }
                    {
                        this.state.detailExpense &&
                        <ExpenseDetailPage
                            expense={this.state.detailExpense.expense}
                            staff={this.state.detailExpense.staff}
                            active={!!this.state.detailExpense}
                            onDismiss={this.handleDetailClose}
                            onUpdated={this.handleExpenseUpdated}
                            onDeleted={this.handleExpenseDeleted}
                        />
                    }
                </ModalPortalHOC>
            </Fragment>
        );
    }
}

ExpensesTab.propTypes = {
    staffList: PropTypes.array
};
